import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Select, FormInstance } from 'antd';
import MultiCascader from 'antd-multi-cascader';
import { NamePath } from 'antd/lib/form/interface';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import 'antd-multi-cascader/dist/index.css';

import { FilterTag } from './filter-tag';
import { debounce as _debounce } from 'lodash';
import { Translations } from '../filter-bar-2';
import { FilterBar2Value } from '../types';
import Checkbox from 'components/checkbox/checkbox';

export interface SelectValue {
	label: string;
	value: any;
	children?: SelectValue;
}

export interface FilterContentSelectProps {
	name: NamePath;
	values?: FilterBar2Value[];
	multiselect?: boolean;
	placeholder?: string;
	className?: string;
	allowClear?: boolean;
	translations: Translations;
	onChange?: (value: any, option: any) => void;
	onSearch?: (value: string, key: NamePath) => Promise<any>;
	form: FormInstance;
}

export const FilterContentSelect = ({
	name,
	values,
	placeholder,
	multiselect,
	className,
	onChange,
	onSearch,
	form,
	allowClear = true
}: FilterContentSelectProps) => {
	// TODO: the only way to make checkboxes be rendered with a actual value
	// easy can be refactored to one line with hook .useWatch from new atnd version
	const [currentValues, setCurrentValues] = useState([]);
	const [listValues, setListValues] = useState(values);
	const selectedValues = form.getFieldValue(name);
	const isCascade = useMemo(() => listValues?.some(item => item.children), [listValues]);

	useEffect(() => {
		setCurrentValues(selectedValues);
	}, [selectedValues]);

	useEffect(() => {
		setListValues(values);
	}, [values]);

	const onValueChange = (value, option) => {
		setCurrentValues(value);
		onChange(value, option);
	};

	const onSearchValue = async (value: string) => {
		const trimmedValue = value.trim();
		if (onSearch && trimmedValue.length > 1) {
			const lookupListValues = await onSearch(trimmedValue, name);
			const newList = listValues?.length ? lookupListValues.concat(listValues) : lookupListValues;
			const uniqueList = [...new Map(newList.map(item => [item.value, item])).values()] as FilterBar2Value[];
			setListValues(uniqueList);
		}
	};

	const cascadeValues = useMemo(() => {
		const process = data =>
			data.map(item => ({
				value: item.value,
				title: item.label,
				children: Array.isArray(item.children) ? process(item.children) : []
			}));

		return process(listValues);
	}, [listValues]);

	const filterOption = (input, option) =>
		option.children?.[1]?.toLowerCase().indexOf(input.toLowerCase().trim()) >= 0;

	const stopPropagationMouseDownEvent = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
		event.stopPropagation();
	}, []);

	if (isCascade) {
		// TODO: we can switch back to this component when updated to ^4.17.0 https://github.com/bringg/react-components/pull/750
		return (
			<Form.Item name={name}>
				<MultiCascader placeholder={placeholder} data={cascadeValues} onChange={onChange} />
			</Form.Item>
		);
	}

	return (
		<Form.Item name={name}>
			<Select
				// TODO: with new antd version we will be able to use our own icon for clear
				allowClear={allowClear}
				showArrow
				className={className}
				mode={multiselect ? 'multiple' : undefined}
				showSearch
				filterOption={filterOption}
				placeholder={placeholder}
				suffixIcon={<BringgIcon className="filter-chevron-icon" iconName={BringgFontIcons.Chevron} />}
				tagRender={props => (
					<FilterTag
						closable
						reversed={false}
						onClose={props.onClose}
						onMouseDown={stopPropagationMouseDownEvent}
					>
						{listValues?.find(val => val.value === props.value)?.label}
					</FilterTag>
				)}
				onSearch={_debounce(onSearchValue, 500)}
				onChange={onValueChange}
				popupClassName={classNames('filter-bar-select', { multiselect })}
				data-test-id={`${name}-select-input`}
			>
				{listValues.map(item => (
					<Select.Option title={item.label} label={item.label} value={item.value} key={item.value}>
						{multiselect && <Checkbox checked={currentValues?.includes(item.value)} />}
						{item.label}
					</Select.Option>
				))}
			</Select>
		</Form.Item>
	);
};
